(() => {
  if (window.__cartUpsellInitialized) return;
  window.__cartUpsellInitialized = true;

  const upsellSelector = '[data-cart-upsell]';

  function updateUpsell(upsell) {
    const selected = Number(upsell.dataset.selected || 0);
    const limit = Number(upsell.dataset.limit || 1);
    const busy = upsell.dataset.busy === 'true';
    const isComplete = selected >= limit;
    const counter = upsell.querySelector('[data-cart-upsell-counter]');
    const completeMessage = upsell.querySelector('[data-cart-upsell-complete]');
    const productList = upsell.querySelector('[data-cart-upsell-list]');

    if (counter) counter.textContent = `${Math.min(selected, limit)}/${limit}`;
    if (completeMessage) completeMessage.hidden = !isComplete;
    if (productList) productList.hidden = isComplete;

    upsell.querySelectorAll('[data-cart-upsell-add]').forEach((button) => {
      button.disabled = busy || isComplete;
      button.toggleAttribute('aria-busy', busy);
    });
  }

  function setStatus(upsell, message = '') {
    const status = upsell.querySelector('[data-cart-upsell-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = message === '';
  }

  function dynamicSourceUrl(upsell) {
    const root = window.Shopify?.routes?.root || '/';
    const source = upsell.dataset.source;

    if (source === 'product_type') {
      const url = new URL(`${root}search`, window.location.origin);
      url.searchParams.set('section_id', 'cart-upsell-search');
      url.searchParams.set('type', 'product');
      if (upsell.dataset.inStockOnly === 'true') {
        url.searchParams.set('options[unavailable_products]', 'hide');
      }
      url.searchParams.set('q', `product_type:"${upsell.dataset.productType || ''}"`);
      return url;
    }

    const url = new URL(`${root}recommendations/products`, window.location.origin);
    url.searchParams.set('section_id', 'cart-upsell-recommendations');
    url.searchParams.set('product_id', upsell.dataset.productId || '');
    url.searchParams.set('limit', upsell.dataset.visibleLimit || '8');
    url.searchParams.set('intent', source);
    return url;
  }

  async function loadDynamicProducts(upsell) {
    const placeholder = upsell.querySelector('[data-cart-upsell-dynamic]');
    if (!placeholder) return;

    try {
      const response = await fetch(dynamicSourceUrl(upsell), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error('Unable to load cart upsell');

      const responseHtml = await response.text();
      const documentFragment = new DOMParser().parseFromString(responseHtml, 'text/html');
      const productList = documentFragment.querySelector('[data-cart-upsell-list]');

      if (!productList?.querySelector('[data-cart-upsell-card]')) {
        upsell.remove();
        return;
      }

      placeholder.replaceWith(document.importNode(productList, true));
      updateUpsell(upsell);
    } catch (error) {
      upsell.remove();
    }
  }

  function initializeUpsell(upsell) {
    if (upsell.dataset.initialized === 'true') return;
    upsell.dataset.initialized = 'true';
    upsell.dataset.busy = 'false';
    updateUpsell(upsell);
    loadDynamicProducts(upsell);
  }

  function scan(root = document) {
    if (root.matches?.(upsellSelector)) initializeUpsell(root);
    root.querySelectorAll?.(upsellSelector).forEach(initializeUpsell);
  }

  async function addUpsellProduct(button, upsell) {
    const selected = Number(upsell.dataset.selected || 0);
    const limit = Number(upsell.dataset.limit || 1);

    if (upsell.dataset.busy === 'true' || selected >= limit) return;

    upsell.dataset.busy = 'true';
    setStatus(upsell);
    updateUpsell(upsell);

    const variantId = Number(button.dataset.variantId);
    const sections = ['variant-added'];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections },
      }),
    );

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { _upsell_carrito: 'true' } }],
          sections,
          sections_url: `${root}variants/${variantId}`,
        }),
      });

      const responseJson = await response.json();
      if (!response.ok) throw new Error(responseJson.description || 'Unable to add upsell product');

      const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
      if (!cartResponse.ok) throw new Error('Unable to refresh cart');

      const cart = await cartResponse.json();
      cart.sections = responseJson.sections;
      upsell.dataset.selected = String(Math.min(selected + 1, limit));
      upsell.dataset.busy = 'false';
      updateUpsell(upsell);

      button.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.items || [responseJson],
            cart,
          },
        }),
      );

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart,
          },
        }),
      );
    } catch (error) {
      upsell.dataset.busy = 'false';
      setStatus(upsell, upsell.dataset.errorMessage || '');
      updateUpsell(upsell);
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-upsell-add]');
    if (!button) return;

    const upsell = button.closest(upsellSelector);
    if (!upsell) return;

    addUpsellProduct(button, upsell);
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) scan(node);
      });
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      scan();
      observer.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    scan();
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
